import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, FormControl, InputLabel,
  Select, Alert, LinearProgress, Pagination, Tooltip,
  Checkbox, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Info as InfoIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { manufacturingAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import usePageTitle from '../../hooks/usePageTitle';

function ItemManagement() {
  const { user } = useAuth();
  
  // Data states
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    raw_material: 0,
    manufactured: 0,
    assembly: 0,
    finished_good: 0
  });

  // UI states
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportPrefix, setExportPrefix] = useState('');
  const [exportCaseSensitive, setExportCaseSensitive] = useState(false);
  const [exportIncludeInactive, setExportIncludeInactive] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [formData, setFormData] = useState({
    item_code: '',
    name: '',
    description: '',
    additional_instructions: '',
    item_type: 'raw_material',
    unit_of_measure: 'EA',
    standard_cost: '',
    last_cost_price: '',
    reorder_level: '',
    weight: ''
  });

  const itemTypes = [
    { value: 'raw_material', label: 'Raw Material' },
    { value: 'manufactured', label: 'Manufactured Part' },
    { value: 'assembly', label: 'Assembly' },
    { value: 'finished_good', label: 'Finished Product' }
  ];

  const unitOptions = ['EA', 'KG', 'M', 'M2', 'L', 'PCS'];
  const pageSize = 25;

  usePageTitle(searchTerm ? `Items: ${searchTerm}` : 'Items');

  // Load data with pagination and filters
  const loadData = useCallback(async (page = 1, search = '', type = '') => {
    try {
      setLoading(true);
      console.log(`Loading page ${page} with search: "${search}" and type: "${type}"`);

      const params = {
        page,
        page_size: pageSize
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      if (type) {
        params.type = type;
      }

      const response = await manufacturingAPI.items.list(params);
      console.log('Items response:', response.data);

      setItems(response.data.results || []);
      setTotalCount(response.data.count || 0);
      setCurrentPage(page);

    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Failed to load items');
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load stats separately for better performance
  const loadStats = useCallback(async () => {
    try {
      const response = await manufacturingAPI.items.listAll();
      const allItems = response.data.results || response.data || [];
      
      const newStats = {
        raw_material: allItems.filter(item => item.item_type === 'raw_material').length,
        manufactured: allItems.filter(item => item.item_type === 'manufactured').length,
        assembly: allItems.filter(item => item.item_type === 'assembly').length,
        finished_good: allItems.filter(item => item.item_type === 'finished_good').length
      };
      
      setStats(newStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadData(1, searchTerm, filterType);
    loadStats();
  }, [loadData, loadStats, searchTerm, filterType]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(localSearchTerm);
    setCurrentPage(1);
  };

  // Handle filter change
  const handleFilterChange = (newFilter) => {
    setFilterType(newFilter);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    loadData(newPage, searchTerm, filterType);
  };

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      loadData(currentPage, searchTerm, filterType),
      loadStats()
    ]);
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // Dialog handlers
  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        item_code: item.item_code,
        name: item.name,
        description: item.description || '',
        additional_instructions: item.additional_instructions || '',
        item_type: item.item_type,
        unit_of_measure: item.unit_of_measure,
        standard_cost: item.standard_cost,
        last_cost_price: item.last_cost_price ?? '',
        reorder_level: item.reorder_level,
        weight: item.weight || ''
      });
    } else {
      setEditingItem(null);
      setFormData({
        item_code: '',
        name: '',
        description: '',
        additional_instructions: '',
        item_type: 'raw_material',
        unit_of_measure: 'EA',
        standard_cost: '',
        last_cost_price: '',
        reorder_level: '',
        weight: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.item_code || !formData.name) {
        toast.error('Item code and name are required');
        return;
      }

      if (!user || !user.id) {
        toast.error('User not found. Please log in again.');
        return;
      }

      const submitData = {
        item_code: formData.item_code,
        name: formData.name,
        description: formData.description || '',
        additional_instructions: formData.additional_instructions || '',
        item_type: formData.item_type,
        unit_of_measure: formData.unit_of_measure,
        standard_cost: parseFloat(formData.standard_cost) || 0,
        // Blank means "no last cost" (null) — the Inventory Value report then
        // falls back to standard cost. 0 is a real (placeholder) value.
        last_cost_price: formData.last_cost_price === '' || formData.last_cost_price === null
          ? null
          : parseFloat(formData.last_cost_price),
        reorder_level: parseFloat(formData.reorder_level) || 0,
        weight: parseFloat(formData.weight) || 0,
        is_active: true,
        created_by: user.id
      };

      if (editingItem) {
        const updateData = { ...submitData };
        delete updateData.created_by;
        await manufacturingAPI.items.update(editingItem.id, updateData);
        toast.success('Item updated successfully');
      } else {
        await manufacturingAPI.items.create(submitData);
        toast.success('Item created successfully');
      }

      handleCloseDialog();
      await Promise.all([
        loadData(currentPage, searchTerm, filterType),
        loadStats()
      ]);
    } catch (error) {
      console.error('Error saving item:', error);
      
      if (error.response?.data) {
        const errors = error.response.data;
        if (typeof errors === 'object') {
          const errorMessages = Object.entries(errors).map(([field, messages]) => 
            `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
          ).join('\n');
          toast.error(`Validation errors:\n${errorMessages}`);
        } else {
          toast.error(errors);
        }
      } else {
        toast.error(editingItem ? 'Failed to update item' : 'Failed to create item');
      }
    }
  };

  const handleDelete = async (item) => {
    if (window.confirm(`Are you sure you want to delete ${item.item_code}?`)) {
      try {
        await manufacturingAPI.items.delete(item.id);
        toast.success('Item deleted successfully');
        await Promise.all([
          loadData(currentPage, searchTerm, filterType),
          loadStats()
        ]);
      } catch (error) {
        console.error('Error deleting item:', error);
        toast.error('Failed to delete item');
      }
    }
  };

  const handleExport = async () => {
    const prefix = exportPrefix.trim();
    if (!prefix) {
      toast.error('Please enter a prefix');
      return;
    }
    setExporting(true);
    try {
      const response = await manufacturingAPI.items.exportByPrefix({
        prefix,
        case_sensitive: exportCaseSensitive,
        include_inactive: exportIncludeInactive,
      });

      const matchCount = parseInt(response.headers?.['x-match-count'] || '0', 10);
      if (!matchCount) {
        toast.warning(`No items found matching prefix "${prefix}"`);
        setExporting(false);
        return;
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const safePrefix = prefix.replace(/[^a-zA-Z0-9]/g, '_') || 'items';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Items_${safePrefix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Exported ${matchCount} item${matchCount === 1 ? '' : 's'}`);
      setExportOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      let msg = 'Failed to generate export';
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.error || msg;
        } catch (e) { /* keep default */ }
      } else if (error?.response?.data?.error) {
        msg = error.response.data.error;
      }
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      'raw_material': 'primary',
      'manufactured': 'secondary',
      'assembly': 'warning',
      'finished_good': 'success'
    };
    return colors[type] || 'default';
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Item Management
          {refreshing && <LinearProgress sx={{ width: 100, ml: 2 }} />}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => setExportOpen(true)}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {itemTypes.map(type => (
          <Grid item xs={12} sm={6} md={3} key={type.value}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                '&:hover': { elevation: 4 },
                backgroundColor: filterType === type.value ? 'primary.light' : 'inherit'
              }}
              onClick={() => handleFilterChange(filterType === type.value ? '' : type.value)}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {type.label}
                  {filterType === type.value && ' (Active Filter)'}
                </Typography>
                <Typography variant="h3" color={getTypeColor(type.value)}>
                  {stats[type.value]}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search and Filter Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box component="form" onSubmit={handleSearch}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search Items"
                  value={localSearchTerm}
                  onChange={(e) => setLocalSearchTerm(e.target.value)}
                  placeholder="Search by item code or name..."
                  InputProps={{
                    endAdornment: (
                      <IconButton type="submit" size="small">
                        <SearchIcon />
                      </IconButton>
                    )
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => handleFilterChange(e.target.value)}
                  label="Filter by Type"
                  startAdornment={<FilterIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {itemTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="textSecondary">
                Showing {items.length} of {totalCount.toLocaleString()} items
                {(searchTerm || filterType) && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSearchTerm('');
                      setLocalSearchTerm('');
                      setFilterType('');
                      setCurrentPage(1);
                    }}
                    sx={{ ml: 1 }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Active Filters Display */}
      {(searchTerm || filterType) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Active filters:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {searchTerm && (
              <Chip
                label={`Search: "${searchTerm}"`}
                onDelete={() => {
                  setSearchTerm('');
                  setLocalSearchTerm('');
                  setCurrentPage(1);
                }}
                size="small"
              />
            )}
            {filterType && (
              <Chip
                label={`Type: ${itemTypes.find(t => t.value === filterType)?.label}`}
                onDelete={() => handleFilterChange('')}
                size="small"
              />
            )}
          </Box>
        </Box>
      )}

      {/* Items Table */}
      <Card>
        {loading && <LinearProgress />}
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Weight (kg)</TableCell>
                <TableCell align="right">Standard Cost</TableCell>
                <TableCell align="right">Last Cost</TableCell>
                <TableCell align="right">Reorder Level</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {item.item_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography variant="body2">
                          {item.name}
                        </Typography>
                        {item.description && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {item.description.length > 50 
                              ? `${item.description.substring(0, 50)}...`
                              : item.description
                            }
                          </Typography>
                        )}
                      </Box>
                      {item.additional_instructions && (
                        <Tooltip 
                          title={
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Additional Instructions:
                              </Typography>
                              <Typography variant="body2">
                                {item.additional_instructions}
                              </Typography>
                            </Box>
                          }
                          arrow
                        >
                          <InfoIcon 
                            fontSize="small" 
                            color="info" 
                            sx={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={itemTypes.find(t => t.value === item.item_type)?.label}
                      color={getTypeColor(item.item_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{item.unit_of_measure}</TableCell>
                  <TableCell align="right">
                    {parseFloat(item.weight || 0) > 0
                      ? `${parseFloat(item.weight).toFixed(2)} kg`
                      : <Typography variant="body2" color="text.secondary">-</Typography>
                    }
                  </TableCell>
                  <TableCell align="right">£{parseFloat(item.standard_cost || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    {item.last_cost_price !== null && item.last_cost_price !== undefined
                      ? `£${parseFloat(item.last_cost_price).toFixed(2)}`
                      : <Typography variant="body2" color="text.secondary">-</Typography>
                    }
                  </TableCell>
                  <TableCell align="right">{item.reorder_level || 0}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(item)}
                      color="primary"
                      title="Edit item"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(item)}
                      color="error"
                      title="Delete item"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Empty State */}
        {!loading && items.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary" gutterBottom>
              {(searchTerm || filterType) 
                ? 'No items match your search criteria' 
                : 'No items found'
              }
            </Typography>
            {!(searchTerm || filterType) && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ mt: 2 }}
              >
                Add Your First Item
              </Button>
            )}
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              showFirstButton
              showLastButton
              disabled={loading}
            />
          </Box>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingItem ? 'Edit Item' : 'Add New Item'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item Code *"
                name="item_code"
                value={formData.item_code}
                onChange={handleInputChange}
                disabled={editingItem !== null}
                helperText={editingItem ? "Item code cannot be changed" : "Unique identifier (e.g., STEEL-ROD-6MM)"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Item Type *</InputLabel>
                <Select
                  name="item_type"
                  value={formData.item_type}
                  onChange={handleInputChange}
                  label="Item Type *"
                >
                  {itemTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Item Name *"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={2}
                helperText="General description of the item"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Instructions"
                name="additional_instructions"
                value={formData.additional_instructions}
                onChange={handleInputChange}
                multiline
                rows={3}
                helperText="Optional manufacturing or handling instructions for this item"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Unit of Measure</InputLabel>
                <Select
                  name="unit_of_measure"
                  value={formData.unit_of_measure}
                  onChange={handleInputChange}
                  label="Unit of Measure"
                >
                  {unitOptions.map(unit => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Standard Cost (£)"
                name="standard_cost"
                type="number"
                value={formData.standard_cost}
                onChange={handleInputChange}
                inputProps={{ step: "0.01", min: "0" }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Last Cost (£)"
                name="last_cost_price"
                type="number"
                value={formData.last_cost_price}
                onChange={handleInputChange}
                inputProps={{ step: "0.0001", min: "0" }}
                helperText="Used by the Inventory Value report; normally from Sage"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Reorder Level"
                name="reorder_level"
                type="number"
                value={formData.reorder_level}
                onChange={handleInputChange}
                inputProps={{ step: "1", min: "0" }}
                helperText="Alert when stock falls below this level"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Weight (kg)"
                name="weight"
                type="number"
                value={formData.weight}
                onChange={handleInputChange}
                inputProps={{ step: "0.01", min: "0" }}
                helperText="Product weight in kilograms"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export by Prefix Dialog */}
      <Dialog
        open={exportOpen}
        onClose={() => !exporting && setExportOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export Items by Prefix</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, mt: 1 }}>
            Export item codes and names for all items whose code starts with the
            given prefix. For example, <strong>XCMP</strong> matches every item
            code beginning with "XCMP"; <strong>E/</strong> matches every item
            code beginning with "E/".
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Item Code Prefix"
            value={exportPrefix}
            onChange={(e) => setExportPrefix(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !exporting) {
                e.preventDefault();
                handleExport();
              }
            }}
            placeholder="e.g. XCMP or E/"
            disabled={exporting}
            sx={{ mb: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={exportCaseSensitive}
                onChange={(e) => setExportCaseSensitive(e.target.checked)}
                disabled={exporting}
              />
            }
            label="Case sensitive"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={exportIncludeInactive}
                onChange={(e) => setExportIncludeInactive(e.target.checked)}
                disabled={exporting}
              />
            }
            label="Include inactive items"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportOpen(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            startIcon={<FileDownloadIcon />}
            disabled={exporting || !exportPrefix.trim()}
          >
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ItemManagement;